import { useState, useEffect } from 'react';
import { Button, Table, Row, Col, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { ToastContainer, toast } from 'react-toastify';
import { getSingleItem, updatePayment } from '../services/Services';
import { getConcepts, getSubConceptsByConcept, updateItemMeta } from '../services/Concepts';

const getUserId = () => localStorage.getItem('userId') ?? 0;

function ChangeItemPaymentModal({ show, handleClose, itemId, handleReload }) {
    let userId = getUserId();
    const [data, setData] = useState(null);
    const [newPay, setNewPay] = useState("");
    const [reason, setReason] = useState("");

    // NUEVO: concepto/subconcepto
    const [concepts, setConcepts] = useState([]);
    const [subConcepts, setSubConcepts] = useState([]);
    const [conceptId, setConceptId] = useState("");      // string para <select>
    const [subConceptId, setSubConceptId] = useState(""); // string para <select>

    // Cargar item + conceptos al abrir
    useEffect(() => {
        if (!itemId) return;

        (async () => {
            try {
                const item = await getSingleItem(itemId);
                setData(item);
                setConceptId(item.concept_id ? String(item.concept_id) : "");
                setSubConceptId(item.subconcept_id ? String(item.subconcept_id) : "");

                const conceptsData = await getConcepts();
                setConcepts(conceptsData || []);

                // si ya tiene concept, trae subconceptos
                if (item.concept_id) {
                    const subs = await getSubConceptsByConcept(item.concept_id);
                    setSubConcepts(subs || []);
                } else {
                    setSubConcepts([]);
                }

            } catch (e) {
                setData(null);
                setConcepts([]);
                setSubConcepts([]);
            }
        })();
    }, [itemId]);

    // Al cambiar concepto => recargar subconceptos
    const handleConceptChange = async (e) => {
        const value = e.target.value; // string
        setConceptId(value);
        setSubConceptId(""); // se limpia, porque ya no aplica

        if (!value) {
            setSubConcepts([]);
            return;
        }

        try {
            const subs = await getSubConceptsByConcept(parseInt(value));
            setSubConcepts(subs || []);
        } catch {
            setSubConcepts([]);
        }
    };

    const handleSubConceptChange = (e) => {
        setSubConceptId(e.target.value);
    };

    const handleReason = (e) => setReason(e.target.value);

    const handleNewPayChange = (e) => {
        const { value } = e.target;
        if (!isNaN(value) && value !== '') setNewPay(value);
        else setNewPay('0');
    };

    const handleSaveAll = async () => {
        const inputsPending = [];

        // validación pago/motivo (igual que ya tienes)
        if (isNaN(newPay) || newPay === '') {
            inputsPending.push("El campo 'Nuevo pago' está vacío o tiene un valor no válido");
        }
        if (!reason) {
            inputsPending.push("El campo 'Motivo' no debe estar vacío");
        }

        // validación concepto/subconcepto
        // (Concepto obligatorio si quieres forzarlo. Si no, quita esta validación.)
        if (!conceptId) {
            inputsPending.push("Debe seleccionar un Concepto");
        }

        // Si hay lista de subconceptos para ese concepto, obligamos a elegir subconcepto
        if (conceptId && subConcepts.length > 0 && !subConceptId) {
            inputsPending.push("Debe seleccionar un Subconcepto");
        }

        if (inputsPending.length) {
            toast.warning(`Debes completar: ${inputsPending.join(', ')}`);
            return;
        }

        try {
            // 1) actualizar pago (lo que ya haces)
            const paymentRes = await updatePayment({
                itemId,
                reason,
                userId,
                pay: parseFloat(newPay)
            });

            if (!paymentRes?.result) {
                toast.error('No se pudo actualizar el pago.');
                return;
            }

            // 2) actualizar concepto/subconcepto (nuevo)
            const metaRes = await updateItemMeta({
                itemId,
                userId,
                conceptId: parseInt(conceptId),
                subConceptId: subConceptId ? parseInt(subConceptId) : null,
                detail: null
            });

            if (!metaRes?.result) {
                toast.error('Pago actualizado, pero no se pudo actualizar Concepto/Subconcepto.');
                return;
            }

            toast.success('Actualizado correctamente');
            handleClose();
            handleReload();

        } catch (e) {
            toast.error('Lo sentimos, algo salió mal.');
        }
    };

    // Para actualizar el pago:
    const handleUpdateNewPay = async () => {
        let inputsPending = [];
        if (isNaN(newPay) || newPay === '') {
            inputsPending.push("El campo 'Nuevo pago' está vacío o tiene un valor no válido")
        }

        if (!reason) {
            inputsPending.push("El campo 'Motivo' no debe estar vacío");
        }

        if (inputsPending.length === 0) {
            let obj = {
                itemId,
                reason,
                userId: parseInt(userId),
                pay: parseFloat(newPay)
            };

            const data = await updatePayment(obj);
            if (data.result) {
                toast.success('Solicitud eliminada correctamente');
                handleClose();
                handleReload();
            } else {
                toast.error('Lo sentimos, algo salió mal.');
            }
        }
        else {
            toast.warning(`Lo sentimos, debes completar los siguientes campos: ${inputsPending.join(', ')}`)
        }
    };

    return (
        <Modal size="xl" show={show} onHide={handleClose} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Movimientos de la Solicitud</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {data ? (
                    <>
                        <Row>
                            <Col xl={12}>
                                <Table className='mb-5 mt-3' border={3} striped bordered hover responsive>
                                    <thead>
                                        <tr className='table-primary'>
                                            <th className='text-center' >Fecha</th>
                                            <th className='text-center'>Concepto</th>
                                            <th className='text-center'>Subconcepto</th>
                                            <th className='text-center'>Descripción</th>
                                            <th className='text-center' >Trabajo desde casa</th>
                                            <th className='text-center' >Horas solicitadas</th>
                                            <th className='text-center' >Horas a pagar</th>
                                            <th className='text-center' >Total a pagar por día</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className='text-center'>{data.date}</td>
                                            <td className='text-center'>{data.concept ?? '-'}</td>
                                            <td className='text-center'>{data.subconcept ?? '-'}</td>
                                            <td className='text-center'>{data.detail ?? '-'}</td>
                                            <td className='text-center'>{data.itsWorkFromHome ? "Si" : "No"}</td>
                                            <td className='text-center'>{data.hours}</td>
                                            <td className='text-center'>{data.hoursToPay}</td>
                                            <td className='text-center'>
                                                {data.modifiedPayment == null ? null : <p className="text-decoration-line-through">$ {data.moneyToPay}</p>}
                                                $ {data.modifiedPayment == null ? data.moneyToPay : data.modifiedPayment}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                        <Row>
                            <Col xl={3} className=' p-2'  >
                                <Form.Label>Nuevo pago:</Form.Label>
                                <Form.Group controlId="formEmail">
                                    <Form.Control
                                        type="text"
                                        name=""
                                        placeholder='$ Únicamente números'
                                        value={newPay}
                                        onChange={handleNewPayChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xl={3} className=' p-2'  >
                                <Form.Label>Ingrese razón del movimiento :</Form.Label>
                                <Form.Group controlId="formEmail">
                                    <Form.Control
                                        type="text"
                                        name=""
                                        placeholder='Motivo'
                                        value={reason}
                                        onChange={handleReason}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            {/* NUEVO: Concepto */}
                            <Col xl={3} className='p-2'>
                                <Form.Label>Concepto:</Form.Label>
                                <Form.Select value={conceptId} onChange={handleConceptChange}>
                                    <option value="">Seleccione...</option>
                                    {concepts.map(c => (
                                        <option key={c.id} value={String(c.id)}>
                                            {c.description}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* NUEVO: Subconcepto */}
                            <Col xl={3} className='p-2'>
                                <Form.Label>Subconcepto:</Form.Label>
                                <Form.Select
                                    value={subConceptId}
                                    onChange={handleSubConceptChange}
                                    disabled={!conceptId || subConcepts.length === 0}
                                >
                                    <option value="">
                                        {(!conceptId)
                                            ? "Seleccione un concepto"
                                            : (subConcepts.length === 0 ? "No aplica" : "Seleccione...")}
                                    </option>

                                    {subConcepts.map(sc => (
                                        <option key={sc.id} value={String(sc.id)}>
                                            {sc.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                    </>
                ) : (
                    'Cargando datos...'
                )}
            </Modal.Body>
            <Modal.Footer>
                {/* <Button onClick={handleUpdateNewPay} variant='success'>Actualizar pago por dia</Button> */}
                <Button onClick={handleSaveAll} variant='success'>Guardar cambios</Button>
                <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ChangeItemPaymentModal;